import 'configuration/ag-grid/ag-grid.init';
import type { ColTypeDef, GetRowIdParams, GridApi, GridReadyEvent } from 'ag-grid-community';
import type { IScheduledQuotesBlotterItem } from 'apiConnector/scheduledRun/scheduledQuotesBlotter';
import { useContextMenuItems } from 'components/shared/Blotter/Grid/Hooks/useContextMenuItems';
import { useDefaultColumnDefinition } from 'components/shared/Blotter/Grid/Hooks/useDefaultColumnDefinition';
import { useDefaultGroupColumnDefinition } from 'components/shared/Blotter/Grid/Hooks/useDefaultColumnGroupDefinition';
import { useRelativeTimeColumnType } from 'components/shared/Blotter/Grid/Hooks/useRelativeTimeColumnType';
import { useServerSideDefaultColumnTypes } from 'components/shared/Blotter/Grid/Hooks/useServerSideDefaultColumnTypes';
import { useTimespanColumnType } from 'components/shared/Blotter/Grid/Hooks/useTimespanColumnType';
import { getHeaderForBlotterIntlKey } from 'components/shared/Blotter/utils';
import {
  runFrequency,
  runStatus,
  scheduledRangeOrDays,
} from 'components/shared/ScheduledRun/Blotter/grid/customColumns';
import { ConnectedScheduledQuotesBlotterGrid } from 'components/shared/ScheduledRun/Blotter/grid/ScheduledQuotesBlotterGrid.connect';
import { useCallback, useMemo, useState } from 'react';

const getHeaderIntlKey = getHeaderForBlotterIntlKey('scheduledQuotesBlotter');

const defaultColumnWidth = { width: 200 };

export function ScheduledQuotesBlotterGrid() {
  const [api, setApi] = useState<GridApi>();
  const onAgGridReady = useCallback((event: GridReadyEvent) => {
    setApi(event.api);
  }, []);

  const defaultColDef = useDefaultColumnDefinition<IScheduledQuotesBlotterItem>(getHeaderIntlKey, defaultColumnWidth);
  const defaultGroupColumnDef = useDefaultGroupColumnDefinition(getHeaderIntlKey);
  const timespanColumn = useTimespanColumnType<IScheduledQuotesBlotterItem>();
  const relativetimeColumn = useRelativeTimeColumnType<IScheduledQuotesBlotterItem>();
  const customColumns = useMemo<Record<string, ColTypeDef<IScheduledQuotesBlotterItem>>>(
    () => ({
      runStatus,
      timespan: timespanColumn,
      relativeTime: relativetimeColumn,
      runFrequency,
      scheduledRangeOrDays,
    }),
    [relativetimeColumn, timespanColumn],
  );
  const columnTypes = useServerSideDefaultColumnTypes<IScheduledQuotesBlotterItem>(customColumns);
  const getContextMenuItems = useContextMenuItems('Blotter Scheduled quotes', api);
  return (
    <ConnectedScheduledQuotesBlotterGrid
      name="scheduledQuotesBlotter"
      logAnalyticName="Blotter Scheduled quotes"
      e2e="scheduled-quotes-blotter"
      onGridReady={onAgGridReady}
      defaultColDef={defaultColDef}
      defaultColGroupDef={defaultGroupColumnDef}
      getRowId={getRowId}
      columnTypes={columnTypes}
      getContextMenuItems={getContextMenuItems}
    />
  );
}

function getRowId(p: GetRowIdParams<IScheduledQuotesBlotterItem>) {
  return p.data.id;
}
